import { useCallback, useMemo, useRef } from "react"

interface UseLatestRequestIdResult {
  /** Claim the next id — call this when a request starts. */
  claim: () => number
  /** Whether `id` is still the most recently claimed one. */
  isCurrent: (id: number) => boolean
  /** Discard whatever is in flight without claiming a new id — call this
   * when the thing the caller was fetching for (a popover, a selection)
   * goes away, so a response already on the wire is dropped rather than
   * applied to what replaced it. */
  invalidate: () => void
}

/**
 * A monotonic counter for dropping a stale response: a request only applies
 * its result if it's still the most recent one claimed by the time it
 * resolves. Guards against a slow fetch landing after the caller moved on to
 * a different id, closed, or unmounted.
 */
export const useLatestRequestId = (): UseLatestRequestIdResult => {
  const ref = useRef(0)

  const claim = useCallback(() => ++ref.current, [])
  const isCurrent = useCallback((id: number) => ref.current === id, [])
  const invalidate = useCallback(() => {
    ref.current++
  }, [])

  // `claim`/`isCurrent`/`invalidate` are each already stable across renders
  // (empty-deps useCallback), but a bare `{ claim, isCurrent, invalidate }`
  // return is still a new object every render — memoized so a caller can
  // safely put the whole result in another hook's dependency array without
  // that hook re-running on every render regardless.
  return useMemo(
    () => ({ claim, isCurrent, invalidate }),
    [claim, isCurrent, invalidate]
  )
}
